import React, { useState, useContext, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { Autocomplete, Box, TextField, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ActionBar from '../../../../common/components/actionBar/ActionBar'
import setNestedValue from 'lodash.set'
import { useBacktestContext } from '../../contexts/BacktestContext'
import { SnackbarHelperContext } from '../../../../contexts/SnackbarHelperContext'
import RemainingBalance from '../../../../components/RemainingBalance'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getClientTimeZone } from '../../../../common/timezones'
import { normalizeSchedule } from '../../../../common/backtestSchedule'

dayjs.extend(utc)
dayjs.extend(timezone)

// Migration function to convert old schema to new schema
const migrateBacktestOption = (option: any) => {
  // Check if this is an old schema object (has startTime/endTime but no daysOfWeek/timeRanges)
  if (
    option.startTime !== undefined &&
    option.endTime !== undefined &&
    (!option.daysOfWeek || !option.timeRanges)
  ) {
    const migratedOption = { ...option }

    // Determine timezone
    let targetTimezone = option.timezone
    if (!targetTimezone) {
      // Use local timezone if not specified
      targetTimezone = getClientTimeZone()
      migratedOption.timezone = targetTimezone
    }

    // Convert startTime and endTime to timeRanges
    let startTimeFormatted: string
    let endTimeFormatted: string

    // convert from UTC to the specified timezone
    startTimeFormatted = dayjs.utc(option.startTime).tz(targetTimezone).format('HH:mm')
    endTimeFormatted = dayjs.utc(option.endTime).tz(targetTimezone).format('HH:mm')

    migratedOption.timeRanges = [
      {
        startTimeRange: startTimeFormatted,
        endTimeRange: endTimeFormatted,
      },
    ]

    // Initialize daysOfWeek to all days of the week
    migratedOption.daysOfWeek = [0, 1, 2, 3, 4, 5, 6]

    // Remove old properties
    delete migratedOption.startTime
    delete migratedOption.endTime

    // Produce new schedule field from migrated timeRanges/daysOfWeek/timezone
    migratedOption.schedule = normalizeSchedule(migratedOption)

    return migratedOption
  }

  // Also normalize v2 schema (has daysOfWeek/timeRanges but no schedule)
  if (!option.schedule && option.daysOfWeek && option.timeRanges) {
    return { ...option, schedule: normalizeSchedule(option) }
  }

  return option
}

function loadAndMigrateOptions() {
  const rawBacktestOptions = JSON.parse(localStorage.getItem('savedBacktestOptions') || '[]')
  const migratedOptions = rawBacktestOptions.map(migrateBacktestOption)
  const needsMigration = migratedOptions.some(
    (option: any, index: number) =>
      JSON.stringify(option) !== JSON.stringify(rawBacktestOptions[index])
  )
  if (needsMigration) {
    localStorage.setItem('savedBacktestOptions', JSON.stringify(migratedOptions))
  }
  return migratedOptions
}

const BacktestHeaderBar: React.FC = () => {
  const { setBacktestSettingInputData, backtestSettingInputData } = useBacktestContext()
  const [backtestOptions, setBacktestOptions] = useState(loadAndMigrateOptions)
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const theme = useTheme()

  useEffect(() => {
    const handleStorageChange = (event: any) => {
      if (event.key === 'savedBacktestOptions') {
        const rawOptions = JSON.parse(event.newValue || '[]')
        const migratedOptions = rawOptions.map(migrateBacktestOption)
        const needsMigration = migratedOptions.some(
          (option: any, index: number) =>
            JSON.stringify(option) !== JSON.stringify(rawOptions[index])
        )

        if (needsMigration) {
          localStorage.setItem('savedBacktestOptions', JSON.stringify(migratedOptions))
        }

        setBacktestOptions(migratedOptions)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  function handleDeleteBacktestOption(op: any) {
    const rawSavedBacktestOptions = JSON.parse(localStorage.getItem('savedBacktestOptions') || '[]')
    const migratedSavedOptions = rawSavedBacktestOptions.map(migrateBacktestOption)
    const newSavedBacktestOptions = migratedSavedOptions.filter(
      (option: any) => option.id !== op.id
    )
    const newValue = JSON.stringify(newSavedBacktestOptions)
    localStorage.setItem('savedBacktestOptions', newValue)
    setBacktestOptions(newSavedBacktestOptions)
    setSnackbarSettings({
      message: 'Backtest settings deleted.',
      severity: 'success',
      autoHideDuration: 6000,
    })
  }

  return (
    <>
      <ActionBar
        left={
          <Box display={'flex'} flexDirection={'row'} gap={2} alignItems={'center'}>
            <Box sx={{ width: '300px' }}>
              <Autocomplete
                freeSolo
                options={backtestOptions}
                value={backtestSettingInputData}
                onChange={(_, val: any) => val && setBacktestSettingInputData(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: theme.palette.background.custom3,
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: '1px',
                        },
                      },
                    }}
                    onChange={(e) => {
                      setBacktestSettingInputData((prevState: any) => {
                        const newState = { ...prevState }
                        setNestedValue(newState, 'name', e.target.value)
                        return newState
                      })
                    }}
                    label="Backtest settings name"
                    fullWidth
                  />
                )}
                getOptionLabel={(option) => (typeof option === 'string' ? '' : option.name)}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <>
                        {option.name}
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleDeleteBacktestOption(option)
                          }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </>
                    </Box>
                  </li>
                )}
              />
            </Box>
          </Box>
        }
        right={
          <Box display={'flex'} alignItems={'center'}>
            <RemainingBalance />
          </Box>
        }
        position={'top'}
      />
    </>
  )
}

export default BacktestHeaderBar
